// src/components/Home/Section5.jsx
import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";

// PNG (fallback) + WEBP (optimizado)
import img1 from "../../assets/img/proyectos/img1.png";
import img1Webp from "../../assets/img/proyectos/img1.webp";
import img2 from "../../assets/img/proyectos/img2.png";
import img2Webp from "../../assets/img/proyectos/img2.webp";
import img3 from "../../assets/img/proyectos/img3.png";
import img3Webp from "../../assets/img/proyectos/img3.webp";
import img4 from "../../assets/img/proyectos/img4.png";
import img4Webp from "../../assets/img/proyectos/img4.webp";
import img5 from "../../assets/img/proyectos/img5.png";
import img5Webp from "../../assets/img/proyectos/img5.webp";
import img6 from "../../assets/img/proyectos/img6.png";
import img6Webp from "../../assets/img/proyectos/img6.webp";
import img7 from "../../assets/img/proyectos/img7.png";
import img7Webp from "../../assets/img/proyectos/img7.webp";

const proyectos = [
  {
    id: 1,
    titulo: "Dispositivo de estiba y montaje de Coriolis en SKID",
    desc: "En RTAG identificamos una operación insegura y poco eficiente en el montaje, desmontaje y almacenamiento de un caudalímetro Coriolis crítico. A partir de un relevamiento en campo, diseñamos y fabricamos un dispositivo a medida que resolvió la problemática, optimizando seguridad, tiempo y cuidado del equipo.",
    link: "https://drive.google.com/file/d/13Gl_zjWHG-4geBbANnbzVHGFXo8UTZ9q/view?usp=drive_link",
    img: img1,
    imgWebp: img1Webp,
  },
  {
    id: 2,
    titulo: "Percha C-hook D&M",
    desc: "En RTAG detectamos una maniobra insegura en el izaje de piezas Crossover de distintos tamaños y pesos. Tras relevar la operación en campo, desarrollamos y fabricamos una percha de izaje a medida, verificada estructuralmente, que garantiza seguridad, eficiencia y una mejora integral en el proceso.",
    link: "https://drive.google.com/file/d/1hlqrJCLnuI65lDLLS8-MuD4MTDcWkc02/view?usp=drive_link",
    img: img2,
    imgWebp: img2Webp,
  },
  {
    id: 3,
    titulo: "Percha Stinger",
    desc: "RTAG detectó una operación insegura en la manipulación de la pieza Stinger durante tareas de mantenimiento. Tras relevar la situación junto al equipo, diseñamos y fabricamos una percha de izaje verificada estructuralmente, que asegura un manejo más seguro, rápido y eficiente, aportando una mejora integral al proceso.",
    link: "https://drive.google.com/file/d/1m2v0L4eQlIh2ofg6NsKiwBoVEchD4LXI/view?usp=drive_link",
    img: img3,
    imgWebp: img3Webp,
  },
  {
    id: 4,
    titulo: "Llaves de Punto para desarme de Cañones",
    desc: "En el taller de mantenimiento de cañones se identificó el uso de llaves inadecuadas, con riesgo de rotura de pines y fatiga del operador. Desde RTAG desarrollamos nuevos modelos de llaves —incluyendo versiones antichispa— que optimizan seguridad, ergonomía y eficiencia, logrando una solución integral validada en campo.",
    link: "https://drive.google.com/file/d/1f6S2WDcrw0-l4NTuVCJflAiQ1yD1e0IY/view?usp=drive_link",
    img: img4,
    imgWebp: img4Webp,
  },
  {
    id: 5,
    titulo: "Ingeniería de Soplante",
    desc: "En una instalación industrial se detectó la necesidad de incorporar un nuevo ventilador de vacío, con ductos desconectados y soportes inadecuados. Desde RTAG desarrollamos la ingeniería para su correcto emplazamiento, rediseño de ductos y nueva soportería, garantizando seguridad, eficiencia y la disponibilidad de dos equipos en operación.",
    link: "https://drive.google.com/file/d/1-iuZAYaDKhazv2wMeHIVNJbA5X5SJO2P/view?usp=drive_link",
    img: img5,
    imgWebp: img5Webp,
  },
  {
    id: 6,
    titulo: "Ingeniería Red Contra Incendios",
    desc: "RTAG desarrolló la ingeniería de una red contra incendios para tanques de almacenamiento de nitrato de amonio, un producto de alto riesgo que exige condiciones seguras según normativa. El proyecto incluyó diseño de sala de bombas, red de cañerías y soportería, garantizando cumplimiento normativo, seguridad y respaldo técnico ante la autoridad de aplicación.",
    link: "https://drive.google.com/file/d/1jKeOvHQRvVn5CD2eQpkTs-L4nM_846_S/view?usp=drive_link",
    img: img6,
    imgWebp: img6Webp,
  },
  {
    id: 7,
    titulo: "Ingeniería de Tanques y Equipos",
    desc: "Ante la falta de documentación técnica completa en tanques y equipos en servicio, RTAG desarrolló la ingeniería necesaria para su rediseño y recuperación. El proyecto incluyó un tanque separador según API 650, un recipiente a presión conforme ASME VIII Div. 1 y la rehabilitación de un tanque fuera de servicio, asegurando cumplimiento normativo, calidad y extensión de vida útil.",
    link: "https://drive.google.com/file/d/1zLXsz4xsMtixhs1_tt3U-bTG_Sg0p8FQ/view?usp=drive_link",
    img: img7,
    imgWebp: img7Webp,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const GAP_PX = 24; // gap-6

const Section5 = () => {
  const trackRef = useRef(null);
  const halfRef = useRef(0);
  const rafRef = useRef(null);
  const isHoveringRef = useRef(false);

  const proyectosDuplicados = useMemo(() => [...proyectos, ...proyectos], []);

  const recalcHalfAndCenter = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const half = el.scrollWidth / 2;
    halfRef.current = half;
    el.scrollLeft = Math.max(0, half / 2);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    recalcHalfAndCenter();

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const half = halfRef.current || el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
        else if (el.scrollLeft <= 0) el.scrollLeft += half;
        rafRef.current = null;
      });
    };

    const onResize = () => recalcHalfAndCenter();

    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [recalcHalfAndCenter]);

  const scrollByCards = useCallback((dir = 1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card='1']");
    const delta = card ? card.clientWidth + GAP_PX : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * delta, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!isHoveringRef.current) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        scrollByCards(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollByCards(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scrollByCards]);

  return (
    <section id="soluciones" className="w-full py-20 sm:py-24">
      <div className="w-full max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="flex items-end justify-end"
        >
          <div className="text-right">
            <h3 className="text-white font-inversionzUnboxed text-[32px] sm:text-[48px] leading-none">
              SOLUCIONES
            </h3>
          </div>
        </motion.div>

        {/* Barra */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="w-screen -mx-[calc(50vw-50%)] mt-3"
        >
          <div className="flex items-center justify-end gap-3 px-6 max-w-[1200px] mx-auto">
            <span className="h-[1px] flex-1 bg-white" />
            <span className="h-[4px] w-16 bg-[#21C0A8]" />
          </div>
        </motion.div>

        {/* Subtítulo */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center text-white/90 italic font-inter text-base sm:text-xl mt-8 sm:mt-10 mb-6 sm:mb-8"
        >
          NUESTROS CASOS DE ÉXITO
        </motion.p>
      </div>

      {/* Carrusel */}
      <div className="relative w-screen -mx-[calc(50vw-50%)]">
        <div
          ref={trackRef}
          className="flex overflow-x-auto overflow-y-hidden gap-6 px-4 sm:px-6 hide-scrollbar snap-x"
          style={{ overscrollBehavior: "contain" }}
          onMouseEnter={() => (isHoveringRef.current = true)}
          onMouseLeave={() => (isHoveringRef.current = false)}
        >
          {proyectosDuplicados.map((p, idx) => (
            <motion.article
              key={`${p.id}-${idx}`}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              data-card="1"
              className="snap-start shrink-0 w-[88%] xs:w-[80%] sm:w-[48%] md:w-[32%] lg:w-[24%]"
            >
              <div className="bg-white rounded-[18px] shadow-md border border-black/10 h-[420px] sm:h-[460px] flex flex-col justify-between">
                {/* Imagen optimizada (WebP + fallback) */}
                <div className="p-3">
                  <div className="rounded-[14px] border border-black/15 p-2 h-38 sm:h-40 grid place-items-center">
                    <picture>
                      <source srcSet={p.imgWebp} type="image/webp" />
                      <img
                        src={p.img}
                        alt={p.titulo}
                        loading="lazy"
                        decoding="async"
                        className="max-w-full max-h-full object-contain"
                      />
                    </picture>
                  </div>
                </div>

                {/* Texto + botón */}
                <div className="px-4 sm:px-5 flex flex-col flex-1">
                  {/* Título con altura fija */}
                  <div className="h-[48px] sm:h-[56px] flex items-center justify-center text-center">
                    <h4 className="text-[#0B0A0B] font-bai font-semibold text-sm sm:text-base">
                      {p.titulo}
                    </h4>
                  </div>

                  {/* Descripción con min y max height */}
                  <div className="mt-1 flex-1 overflow-y-auto min-h-[100px] sm:min-h-[120px] max-h-[140px] sm:max-h-[160px] mb-3 text-scroll text-center">
                    <p className="text-[#0B0A0B]/70 text-[11px] sm:text-[13px] leading-relaxed">
                      {p.desc}
                    </p>
                  </div>

                  {/* Botón */}
                  <div className="mt-auto mb-3 sm:mb-5 flex items-center justify-center">
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-4 py-2 rounded-full bg-black text-white text-xs sm:text-sm font-bai hover:bg-[#111] transition"
                    >
                      Ver proyecto
                    </a>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Controles */}
        <div className="flex flex-col items-center justify-center gap-6 mt-6 sm:mt-8">
          <div className="flex items-center justify-center gap-5">
            <button
              onClick={() => scrollByCards(-1)}
              className="h-11 w-11 rounded-full border border-white/30 text-white grid place-items-center hover:bg-white/10 transition"
              aria-label="Anterior"
            >
              ←
            </button>
            <button
              onClick={() => scrollByCards(1)}
              className="h-11 w-11 rounded-full border border-white/30 text-white grid place-items-center hover:bg-white/10 transition"
              aria-label="Siguiente"
            >
              →
            </button>
          </div>

          {/* CTA global */}
          <a
            href="https://drive.google.com/file/d/1xFqKqC8xrpcyJI3vCH-yHo3-Bn9rGdd2/view?usp=drive_link"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3 rounded-full bg-[#33A080] text-white font-bai text-sm sm:text-base hover:bg-[#2a7f66] transition"
          >
            Ver todos los proyectos
          </a>
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .text-scroll::-webkit-scrollbar { width: 6px; }
        .text-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.25); border-radius: 8px; }
        .text-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </section>
  );
};

export default Section5;